/**
 * Module landing pages — the "Overview" tab for each of the 7 modules.
 *
 * Per DESIGN_SYSTEM.md §1 "Module landing pattern" (LOCKED): every
 * module's root URL `/{module}/` opens a meaningful at-a-glance
 * summary, not a silent redirect to whichever sub-page exists. Each
 * landing presents key counts, recent activity (where data is
 * available), and 3-4 quick-action buttons.
 *
 * Mission Control already has its own landing page
 * (`MissionControlPage`) — these are the six other modules: People,
 * Operations, Policies, AI, Reports, Settings.
 *
 * Implementation notes:
 *   - Each landing composes existing endpoints + design-system
 *     primitives (PageHeader, EmptyState, Skeleton, etc.). No new
 *     backend.
 *   - Where a recent-activity feed is in spec but no endpoint
 *     exists yet (e.g. People > recent hires), the landing shows
 *     the existing count and a "Coming soon" placeholder so the
 *     IA is visible without faking data.
 *   - All counts share the same MetricCard look — small at-a-
 *     glance tile, click-through to the relevant tab.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  AlertCircle,
  ArrowRight,
  BadgeDollarSign,
  Banknote,
  Bell,
  Briefcase,
  Building2,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  Clock,
  CreditCard,
  Database,
  Eye,
  Gauge,
  GitBranch,
  Layers,
  Network,
  Package,
  Palette,
  Play,
  Plug,
  ShieldAlert,
  Sparkles,
  TrendingUp,
  UploadCloud,
  UserCog,
  UserPlus,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { Loading } from "../../components/Loading";
import {
  EmptyState,
  PageHeader,
  SettingsEntryCard,
  SettingsSection,
} from "../../components/ui";
import type { KPITone } from "../../components/ui";
import { api } from "../../lib/api";
import { useLegalEntityId } from "../../lib/legalEntity";

// ---------------------------------------------------------------------------
// Shared atoms
// ---------------------------------------------------------------------------

function MetricCard({
  icon: Icon,
  label,
  value,
  to,
  hint,
}: {
  icon: LucideIcon;
  label: string;
  value: number | string;
  to?: string;
  hint?: string;
}) {
  const inner = (
    <div className="flex h-full flex-col justify-between gap-3 rounded-xl border border-border-subtle bg-surface-default p-5 transition-shadow hover:shadow-sm">
      <header className="flex items-center justify-between">
        <span className="rounded-md bg-surface-sunken p-2 text-text-secondary">
          <Icon size={18} aria-hidden />
        </span>
        {to && (
          <ArrowRight
            size={18}
            aria-hidden
            className="text-text-tertiary transition-transform group-hover:translate-x-0.5"
          />
        )}
      </header>
      <div className="flex flex-col gap-0.5">
        <div className="text-3xl font-bold tabular-nums leading-none text-text-primary">
          {value}
        </div>
        <div className="text-sm font-semibold text-text-primary">{label}</div>
        {hint && (
          <div className="text-xs text-text-secondary">{hint}</div>
        )}
      </div>
    </div>
  );
  if (to) {
    return (
      <Link to={to} className="group block">
        {inner}
      </Link>
    );
  }
  return inner;
}

function QuickActions({
  title,
  actions,
}: {
  title?: string;
  actions: Array<{ to: string; label: string; icon: LucideIcon }>;
}) {
  return (
    <section className="rounded-xl border border-border-subtle bg-surface-default p-5">
      {title && (
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-secondary">
          {title}
        </h2>
      )}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {actions.map(({ to, label, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-border-subtle bg-surface-default px-3 py-2.5 text-sm font-medium text-text-primary transition-colors hover:bg-surface-sunken"
          >
            <Icon size={16} aria-hidden className="text-text-tertiary" />
            {label}
          </Link>
        ))}
      </div>
    </section>
  );
}

function SoonPanel({ title, body }: { title: string; body: string }) {
  return (
    <section className="rounded-xl border border-dashed border-border-subtle bg-surface-default p-5">
      <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
      <p className="mt-1 text-xs italic text-text-tertiary">{body}</p>
    </section>
  );
}

// ===========================================================================
// People — /people
// ===========================================================================

export function PeopleLandingPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const customersQ = useQuery({
    queryKey: ["customers"],
    queryFn: () => api.listCustomers(),
  });
  const customer = customersQ.data?.[0];

  const employeesQ = useQuery({
    queryKey: ["employees", customer?.id],
    queryFn: () => api.listEmployeesForCustomer(customer!.id),
    enabled: Boolean(customer),
  });

  const positionsQ = useQuery({
    queryKey: ["positions", le],
    queryFn: () => api.listPositions(le),
    enabled: !!le,
  });

  const jobsQ = useQuery({
    queryKey: ["jobs", le],
    queryFn: () => api.listJobs(le),
    enabled: !!le,
  });

  if (customersQ.isLoading) return <Loading />;

  const employees = employeesQ.data ?? [];
  const positions = positionsQ.data ?? [];
  const jobs = jobsQ.data ?? [];

  const activeEmployees = employees.filter(
    (e) => e.employment_status === "active",
  ).length;
  const filledPositions = positions.filter((p) => p.status === "filled").length;
  const openPositions = positions.filter((p) => p.status === "open").length;

  return (
    <div className="flex flex-col gap-5">
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          icon={Users}
          label={t("policies.landing_people_active_employees")}
          value={activeEmployees}
          hint={t("policies.landing_people_total_suffix", { count: employees.length })}
          to="/people/employees"
        />
        <MetricCard
          icon={Briefcase}
          label={t("policies.landing_people_positions")}
          value={positions.length}
          hint={t("policies.landing_people_positions_hint", { filled: filledPositions, open: openPositions })}
          to="/people/positions"
        />
        <MetricCard
          icon={Network}
          label={t("policies.landing_people_org_chart")}
          value={positions.length > 0 ? t("policies.landing_people_org_chart_view") : "—"}
          to="/people/org-chart"
          hint={t("policies.landing_people_org_chart_hint")}
        />
        <MetricCard
          icon={ClipboardList}
          label={t("policies.landing_people_jobs")}
          value={jobs.length}
          to="/people/jobs"
          hint={t("policies.landing_people_jobs_hint")}
        />
      </section>

      <QuickActions
        title={t("policies.landing_quick_actions")}
        actions={[
          { to: "/people/employees/new", label: t("policies.landing_people_hire_employee"), icon: UserPlus },
          { to: "/people/positions", label: t("policies.landing_people_browse_positions"), icon: Briefcase },
          { to: "/people/org-chart", label: t("policies.landing_people_view_org_chart"), icon: Network },
          { to: "/people/jobs", label: t("policies.landing_people_manage_jobs"), icon: ClipboardList },
        ]}
      />

      <SoonPanel
        title={t("policies.landing_recent_activity")}
        body={t("policies.landing_recent_activity_body")}
      />
    </div>
  );
}

// ===========================================================================
// Operations — /operations
// ===========================================================================

export function OperationsLandingPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const approvalsQ = useQuery({
    queryKey: ["mc-pending-approvals", le ?? "_all_"],
    queryFn: () =>
      api.missionControlPendingApprovals({
        legalEntityId: le || undefined,
      }),
  });

  const aiActivityQ = useQuery({
    queryKey: ["mc-ai-activity", le ?? "_all_"],
    queryFn: () => api.missionControlAiActivity(le || undefined, 24),
  });

  const approvals = approvalsQ.data ?? [];
  const byType = approvals.reduce<Record<string, number>>((acc, a) => {
    acc[a.workflow_type] = (acc[a.workflow_type] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <div className="flex flex-col gap-5">
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          icon={ClipboardCheck}
          label={t("policies.landing_ops_pending_leave")}
          value={byType["leave_request"] ?? 0}
          to="/operations/leave/approvals"
        />
        <MetricCard
          icon={Banknote}
          label={t("policies.landing_ops_pending_loan")}
          value={byType["loan_application"] ?? 0}
          to="/operations/loans/applications"
        />
        <MetricCard
          icon={Clock}
          label={t("policies.landing_ops_pending_excuse")}
          value={byType["excuse_request"] ?? 0}
          to="/operations/excuses/queue"
        />
        <MetricCard
          icon={UserCog}
          label={t("policies.landing_ops_pending_separations")}
          value={byType["termination"] ?? 0}
          to="/operations/eos"
        />
      </section>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <MetricCard
          icon={CreditCard}
          label={t("policies.landing_ops_payroll")}
          value={t("policies.landing_ops_payroll_value")}
          hint={t("policies.landing_ops_payroll_hint")}
          to="/operations/payroll"
        />
        <MetricCard
          icon={Sparkles}
          label={t("policies.landing_ops_ai_activity_24h")}
          value={aiActivityQ.data?.summary.handled_count ?? 0}
          hint={
            aiActivityQ.data
              ? t("policies.landing_ops_n_escalations", { count: aiActivityQ.data.summary.escalated_count })
              : "—"
          }
          to="/ai/activity-log"
        />
        <MetricCard
          icon={CalendarDays}
          label={t("policies.landing_ops_leave_calendar")}
          value={t("policies.landing_ops_view")}
          hint={t("policies.landing_ops_team_schedule")}
          to="/operations/leave/calendar"
        />
      </section>

      <QuickActions
        title={t("policies.landing_quick_actions")}
        actions={[
          {
            to: "/operations/leave/approvals",
            label: t("policies.landing_ops_review_approvals"),
            icon: ClipboardCheck,
          },
          { to: "/operations/payroll", label: t("policies.landing_ops_check_payroll"), icon: CreditCard },
          { to: "/operations/attendance", label: t("policies.landing_ops_time_attendance"), icon: Clock },
          {
            to: "/operations/leave/calendar",
            label: t("policies.landing_ops_leave_calendar_short"),
            icon: CalendarDays,
          },
        ]}
      />
    </div>
  );
}

// ===========================================================================
// Policies — /policies
// ===========================================================================

export function PoliciesLandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const le = useLegalEntityId();

  const gradesQ = useQuery({
    queryKey: ["grades", le],
    queryFn: () => api.listGrades(le),
    enabled: !!le,
  });
  const allowancesQ = useQuery({
    queryKey: ["allowances", le],
    queryFn: () => api.listAllowances(le),
    enabled: !!le,
  });
  const leaveTypesQ = useQuery({
    queryKey: ["leave-types", le],
    queryFn: () => api.listLeaveTypes(le),
    enabled: !!le,
  });
  const loanProductsQ = useQuery({
    queryKey: ["loan-products", le],
    queryFn: () => api.listLoanProducts(le),
    enabled: !!le,
  });
  const shiftsQ = useQuery({
    queryKey: ["shifts", le],
    queryFn: () => api.listShifts(le),
    enabled: !!le,
  });
  const excuseTypesQ = useQuery({
    queryKey: ["excuse-types", le],
    queryFn: () => api.listExcuseTypes(le),
    enabled: !!le,
  });

  const catalogTotal =
    (gradesQ.data?.length ?? 0) +
    (allowancesQ.data?.length ?? 0) +
    (leaveTypesQ.data?.length ?? 0) +
    (loanProductsQ.data?.length ?? 0) +
    (shiftsQ.data?.length ?? 0) +
    (excuseTypesQ.data?.length ?? 0);

  const catalogsLoading =
    gradesQ.isLoading ||
    allowancesQ.isLoading ||
    leaveTypesQ.isLoading ||
    loanProductsQ.isLoading ||
    shiftsQ.isLoading ||
    excuseTypesQ.isLoading;

  return (
    <div className="flex flex-col gap-5">
      {!catalogsLoading && catalogTotal === 0 && (
        <EmptyState
          headline={t("policies.landing_pol_empty_headline")}
          description={t("policies.landing_pol_empty_body")}
          action={{
            label: t("policies.landing_pol_upload_pdf"),
            onClick: () => navigate("/policies/ai-ingestion"),
          }}
        />
      )}
      <section className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <MetricCard
          icon={GitBranch}
          label={t("policies.landing_pol_grades")}
          value={gradesQ.data?.length ?? 0}
          to="/policies/grades"
        />
        <MetricCard
          icon={BadgeDollarSign}
          label={t("policies.landing_pol_allowances")}
          value={allowancesQ.data?.length ?? 0}
          to="/policies/allowances"
        />
        <MetricCard
          icon={CalendarDays}
          label={t("policies.landing_pol_leave_types")}
          value={leaveTypesQ.data?.length ?? 0}
          to="/policies/leave-types"
        />
        <MetricCard
          icon={Banknote}
          label={t("policies.landing_pol_loan_products")}
          value={loanProductsQ.data?.length ?? 0}
          to="/policies/loan-products"
        />
        <MetricCard
          icon={Clock}
          label={t("policies.landing_pol_shifts")}
          value={shiftsQ.data?.length ?? 0}
          to="/policies/shifts"
        />
        <MetricCard
          icon={ShieldAlert}
          label={t("policies.landing_pol_excuse_types")}
          value={excuseTypesQ.data?.length ?? 0}
          to="/policies/excuse-types"
        />
      </section>

      <QuickActions
        title={t("policies.landing_quick_actions")}
        actions={[
          {
            to: "/policies/ai-ingestion",
            label: t("policies.landing_pol_upload_pdf"),
            icon: UploadCloud,
          },
          { to: "/policies/grades", label: t("policies.landing_pol_edit_grades"), icon: GitBranch },
          {
            to: "/policies/leave-types",
            label: t("policies.landing_pol_manage_leave_types"),
            icon: CalendarDays,
          },
          {
            to: "/policies/allowances",
            label: t("policies.landing_pol_configure_allowances"),
            icon: BadgeDollarSign,
          },
        ]}
      />

      <SoonPanel
        title={t("policies.landing_pol_recent_changes")}
        body={t("policies.landing_pol_recent_changes_body")}
      />
    </div>
  );
}

// ===========================================================================
// AI — /ai
// ===========================================================================

export function AILandingPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const aiActivityQ = useQuery({
    queryKey: ["mc-ai-activity", le ?? "_all_"],
    queryFn: () => api.missionControlAiActivity(le || undefined, 24),
  });

  return (
    <div className="flex flex-col gap-5">
      <section
        className="flex flex-col gap-2 rounded-xl border p-6"
        style={{
          backgroundColor: "var(--ai-accent-bg)",
          borderColor: "var(--ai-accent-subtle)",
        }}
      >
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider"
          style={{ color: "var(--ai-accent)" }}
        >
          <Sparkles size={14} aria-hidden />
          {t("policies.landing_ai_autonomy_mode")}
        </div>
        <div className="text-3xl font-bold text-text-primary">{t("policies.landing_ai_autonomy_standard")}</div>
        <p className="text-sm text-text-secondary">
          {t("policies.landing_ai_autonomy_body")}
        </p>
        <div className="mt-2">
          <Link
            to="/ai/behavior"
            className="inline-flex items-center gap-1.5 rounded-md bg-surface-default px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-surface-sunken"
          >
            {t("policies.landing_ai_change_autonomy")}
            <ArrowRight size={14} aria-hidden className="rtl:rotate-180" />
          </Link>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <MetricCard
          icon={Sparkles}
          label={t("policies.landing_ai_actions_24h")}
          value={aiActivityQ.data?.summary.handled_count ?? 0}
          to="/ai/activity-log"
        />
        <MetricCard
          icon={AlertCircle}
          label={t("policies.landing_ai_escalations_pending")}
          value={aiActivityQ.data?.summary.escalated_count ?? 0}
          to="/ai/stuck-inbox"
        />
        <MetricCard
          icon={Play}
          label={t("policies.landing_ai_sandbox")}
          value={t("policies.landing_ai_sandbox_open")}
          hint={t("policies.landing_ai_sandbox_hint")}
          to="/ai/sandbox"
        />
      </section>

      <QuickActions
        title={t("policies.landing_quick_actions")}
        actions={[
          { to: "/ai/sandbox", label: t("policies.landing_ai_open_sandbox"), icon: Play },
          { to: "/ai/behavior", label: t("policies.landing_ai_change_autonomy"), icon: Gauge },
          { to: "/ai/activity-log", label: t("policies.landing_ai_view_activity_log"), icon: Eye },
          { to: "/ai/behavior", label: t("policies.landing_ai_configure_behavior"), icon: Sparkles },
        ]}
      />

      <SoonPanel
        title={t("policies.landing_ai_recent_config_changes")}
        body={t("policies.landing_ai_recent_config_changes_body")}
      />
    </div>
  );
}

// ===========================================================================
// Reports — /reports
// ===========================================================================

/**
 * Reports library landing (M3X.UIPOLISH PR 9 — closes §0.8).
 *
 * The prior landing used `MetricCard` with `value="Open"` — that
 * made "Open" render as the big headline, looking like the
 * report name. Replaced with the `SettingsEntryCard` pattern from
 * PR 1 so the category name is the headline + the report count
 * is the meta line.
 *
 * Per spec §1.B.1: the library spans ~150 reports across 8
 * domains (5 packs + cross-pack Mutaba'aa + MC + AI Insights).
 * PR 10 wires the missing Attendance + Payroll + cross-pack
 * domain pages; PR 12-14 ship the per-report cards.
 */
export function ReportsLandingPage() {
  const { t } = useTranslation();

  const domains: HubCardDef[] = [
    {
      sectionKey: "core_dashboards",
      title: t("policies.landing_reports_headcount"),
      description: t("policies.landing_reports_headcount_hint"),
      to: "/reports/headcount",
      icon: Users,
      tone: "indigo",
    },
    {
      sectionKey: "core_dashboards",
      title: t("policies.landing_reports_turnover"),
      description: t("policies.landing_reports_turnover_hint"),
      to: "/reports/turnover",
      icon: TrendingUp,
      tone: "amber",
    },
    {
      sectionKey: "core_dashboards",
      title: t("policies.landing_reports_compensation"),
      description: t("policies.landing_reports_compensation_hint"),
      to: "/reports/compensation",
      icon: BadgeDollarSign,
      tone: "indigo",
    },
    {
      sectionKey: "core_dashboards",
      title: t("policies.landing_reports_compliance"),
      description: t("policies.landing_reports_compliance_hint"),
      to: "/reports/compliance",
      icon: ShieldAlert,
      tone: "rose",
    },
    {
      sectionKey: "domain_libraries",
      title: t("policies.landing_reports_loans", "Loans"),
      description: t(
        "policies.landing_reports_loans_hint",
        "Portfolio, arrears, Article 90, refinance",
      ),
      to: "/reports/loans",
      icon: CreditCard,
      tone: "indigo",
      meta: "9 reports",
    },
    {
      sectionKey: "domain_libraries",
      title: t("policies.landing_reports_leaves", "Leaves"),
      description: t(
        "policies.landing_reports_leaves_hint",
        "Balances, encashment, accruals, types",
      ),
      to: "/reports/leaves",
      icon: CalendarDays,
      tone: "green",
      meta: "30 reports",
    },
    {
      sectionKey: "domain_libraries",
      title: t("policies.landing_reports_eos", "End of Service"),
      description: t(
        "policies.landing_reports_eos_hint",
        "Settlements, Article 80/81/84/85/90 firings",
      ),
      to: "/reports/eos",
      icon: Gauge,
      tone: "purple",
      meta: "30 reports",
    },
    {
      sectionKey: "domain_libraries",
      title: t("reports_lib.attendance", "Attendance"),
      description: t(
        "reports_lib.attendance_hint",
        "Late, OT, excuses, shift compliance",
      ),
      to: "/reports/attendance",
      icon: Clock,
      tone: "teal",
      meta: "30 reports",
    },
    {
      sectionKey: "domain_libraries",
      title: t("reports_lib.payroll", "Payroll"),
      description: t(
        "reports_lib.payroll_hint",
        "Salary sheet, 7 cuts, GL, WPS, GOSI, Z25",
      ),
      to: "/reports/payroll",
      icon: Banknote,
      tone: "indigo",
      meta: "38 reports",
    },
    {
      sectionKey: "domain_libraries",
      title: t("reports_lib.cross_pack", "Cross-pack insights"),
      description: t(
        "reports_lib.cross_pack_hint",
        "Mutaba'aa article firings, AI activity, Mission Control aggregates",
      ),
      to: "/reports/cross-pack",
      icon: Sparkles,
      tone: "purple",
      meta: "~15 reports",
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("reports_lib.title", "Reports")}
        subtitle={t(
          "reports_lib.subtitle",
          "150+ reports across loans, leaves, EOS, attendance, payroll, and compliance.",
        )}
      />

      <SettingsSection
        title={t("reports_lib.section_core", "Core dashboards")}
      >
        {domains
          .filter((d) => d.sectionKey === "core_dashboards")
          .map((d) => (
            <SettingsEntryCard
              key={d.title}
              icon={d.icon}
              title={d.title}
              description={d.description}
              to={d.to}
              tone={d.tone}
              meta={d.meta}
              comingSoon={d.comingSoon}
            />
          ))}
      </SettingsSection>

      <SettingsSection
        title={t("reports_lib.section_domain", "Browse by domain")}
      >
        {domains
          .filter((d) => d.sectionKey === "domain_libraries")
          .map((d) => (
            <SettingsEntryCard
              key={d.title}
              icon={d.icon}
              title={d.title}
              description={d.description}
              to={d.to}
              tone={d.tone}
              meta={d.meta}
              comingSoon={d.comingSoon}
            />
          ))}
      </SettingsSection>

      <SettingsSection
        title={t("reports_lib.section_quick", "Library tools")}
      >
        <SettingsEntryCard
          icon={Eye}
          title={t("reports_lib.saved_views", "Saved views")}
          description={t(
            "reports_lib.saved_views_desc",
            "Pre-filtered configurations you've bookmarked.",
          )}
          to="/reports/saved"
          tone="neutral"
        />
        <SettingsEntryCard
          icon={Clock}
          title={t("reports_lib.scheduled", "Scheduled reports")}
          description={t(
            "reports_lib.scheduled_desc",
            "Recurring email delivery (weekly / monthly / quarterly).",
          )}
          to="/reports/scheduled"
          tone="neutral"
        />
        <SettingsEntryCard
          icon={Package}
          title={t("reports_lib.exports", "Past exports")}
          description={t(
            "reports_lib.exports_desc",
            "Re-download any prior export.",
          )}
          to="/reports/exports"
          tone="neutral"
        />
        <SettingsEntryCard
          icon={GitBranch}
          title={t("reports_lib.custom_builder", "Custom builder")}
          description={t(
            "reports_lib.custom_builder_desc",
            "Build your own report from any combination of dimensions.",
          )}
          to="/reports/custom-builder"
          tone="purple"
        />
      </SettingsSection>
    </div>
  );
}

// ===========================================================================
// Settings hub (M3X.UIPOLISH PR 1) — /settings
// ===========================================================================

/**
 * Locked section order on the Settings hub. The 11-key catalog
 * comes from spec §1.A.1; section titles render via t().
 *
 * Section keys are stable identifiers used by the cards table
 * below — DO NOT rename without updating the cards too.
 */
interface SettingsSectionDef {
  key: string;
  titleKey: string;
}

const SETTINGS_SECTIONS: SettingsSectionDef[] = [
  { key: "tenant_org", titleKey: "settings_hub.section_tenant_org" },
  { key: "people_roles", titleKey: "settings_hub.section_people_roles" },
  { key: "policies_loans", titleKey: "settings_hub.section_policies_loans" },
  { key: "policies_leaves", titleKey: "settings_hub.section_policies_leaves" },
  { key: "policies_eos", titleKey: "settings_hub.section_policies_eos" },
  {
    key: "policies_attendance",
    titleKey: "settings_hub.section_policies_attendance",
  },
  { key: "policies_payroll", titleKey: "settings_hub.section_policies_payroll" },
  { key: "government", titleKey: "settings_hub.section_government" },
  { key: "ai_config", titleKey: "settings_hub.section_ai_config" },
  { key: "hardware", titleKey: "settings_hub.section_hardware" },
  { key: "imports", titleKey: "settings_hub.section_imports" },
  { key: "system", titleKey: "settings_hub.section_system" },
];

/**
 * Per-card definition. Each card belongs to one section (sectionKey
 * matches SETTINGS_SECTIONS.key). PR 2-8 progressively add cards
 * here; PR 8 ships the final ~50-card catalog.
 */
interface HubCardDef {
  sectionKey: string;
  title: string;
  description: string;
  to: string;
  icon: LucideIcon;
  meta?: string;
  tone?: KPITone;
  comingSoon?: boolean;
}



/**
 * Settings hub (M3X.UIPOLISH PR 1).
 *
 * Per spec §1.A.1: every per-LE configurable surface in the system
 * surfaces here as a SettingsEntryCard, organized in ~10 sections.
 * Replaces the prior thin MetricCard + QuickActions shell.
 *
 * PR 1 ships the SCAFFOLD: PageHeader + search box + section grid
 * with EMPTY sections that PR 2-8 fill in (Tenant + HR Policies
 * × 5 + Government + AI + Hardware + Imports + System).
 *
 * Each section is bilingual via t("settings_hub.section_*"); each
 * card uses <SettingsEntryCard /> with bilingual title + description
 * + icon + tone + meta. Search filters the card grid in real time.
 */
export function SettingsLandingPage() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");

  // Live counts for the cards that benefit from them (legal entities,
  // loan products, leave types, etc.). Quietly fall back to undefined
  // when the endpoint isn't authorised/available — the card still
  // renders without the meta line.
  const entitiesQ = useQuery({
    queryKey: ["legal-entities"],
    queryFn: () => api.listOnboardingEntities(),
    retry: false,
  });
  const entitiesCount = entitiesQ.data?.entities?.length;

  // The locked ~50-card catalog (PR 2-8 bundle). Each card lives in
  // exactly one section. Coming-soon cards keep the IA discoverable
  // for surfaces whose dedicated pages land in a follow-up.
  const allCards: HubCardDef[] = useMemo(() => {
    const cards: HubCardDef[] = [
      // --- Tenant + Organization (5) ---
      {
        sectionKey: "tenant_org",
        title: t("settings_hub.card_tenant_title"),
        description: t("settings_hub.card_tenant_desc"),
        to: "/settings/tenant",
        icon: Building2,
        tone: "indigo",
      },
      {
        sectionKey: "tenant_org",
        title: t("settings_hub.card_legal_entities_title"),
        description: t("settings_hub.card_legal_entities_desc"),
        to: "/settings/legal-entities",
        icon: Layers,
        tone: "indigo",
        meta:
          entitiesCount !== undefined
            ? `${entitiesCount} ${entitiesCount === 1 ? "entity" : "entities"}`
            : undefined,
      },
      {
        sectionKey: "tenant_org",
        title: t("settings_hub.card_customer_groups_title"),
        description: t("settings_hub.card_customer_groups_desc"),
        to: "/settings/legal-entities",
        icon: Users,
        tone: "indigo",
        comingSoon: true,
      },
      {
        sectionKey: "tenant_org",
        title: t("settings_hub.card_branding_title"),
        description: t("settings_hub.card_branding_desc"),
        to: "/settings/branding",
        icon: Palette,
        tone: "purple",
      },
      {
        sectionKey: "tenant_org",
        title: t("settings_hub.card_defaults_title"),
        description: t("settings_hub.card_defaults_desc"),
        to: "/settings/tenant",
        icon: Sparkles,
        tone: "teal",
      },

      // --- People + Roles (4) ---
      {
        sectionKey: "people_roles",
        title: t("settings_hub.card_users_roles_title"),
        description: t("settings_hub.card_users_roles_desc"),
        to: "/settings/users",
        icon: UserCog,
        tone: "indigo",
      },
      {
        sectionKey: "people_roles",
        title: t("settings_hub.card_approval_chains_title"),
        description: t("settings_hub.card_approval_chains_desc"),
        to: "/policies/approval-chains",
        icon: GitBranch,
        tone: "teal",
      },
      {
        sectionKey: "people_roles",
        title: t("settings_hub.card_delegation_title"),
        description: t("settings_hub.card_delegation_desc"),
        to: "/policies/approval-chains",
        icon: UserPlus,
        tone: "neutral",
        comingSoon: true,
      },
      {
        sectionKey: "people_roles",
        title: t("settings_hub.card_notifications_title"),
        description: t("settings_hub.card_notifications_desc"),
        to: "/settings/notifications",
        icon: Bell,
        tone: "amber",
      },

      // --- HR Policies — Loans (5) ---
      {
        sectionKey: "policies_loans",
        title: t("settings_hub.card_loan_products_title"),
        description: t("settings_hub.card_loan_products_desc"),
        to: "/policies/loan-products",
        icon: CreditCard,
        tone: "indigo",
      },
      {
        sectionKey: "policies_loans",
        title: t("settings_hub.card_loan_chains_title"),
        description: t("settings_hub.card_loan_chains_desc"),
        to: "/policies/approval-chains/loan_application",
        icon: GitBranch,
        tone: "indigo",
      },
      {
        sectionKey: "policies_loans",
        title: t("settings_hub.card_article_90_title"),
        description: t("settings_hub.card_article_90_desc"),
        to: "/policies/loan-products",
        icon: ShieldAlert,
        tone: "amber",
        comingSoon: true,
      },
      {
        sectionKey: "policies_loans",
        title: t("settings_hub.card_article_95_title"),
        description: t("settings_hub.card_article_95_desc"),
        to: "/policies/loan-products",
        icon: ShieldAlert,
        tone: "rose",
        comingSoon: true,
      },
      {
        sectionKey: "policies_loans",
        title: t("settings_hub.card_sharia_title"),
        description: t("settings_hub.card_sharia_desc"),
        to: "/policies/loan-products",
        icon: BadgeDollarSign,
        tone: "teal",
        comingSoon: true,
      },

      // --- HR Policies — Leaves (5) ---
      {
        sectionKey: "policies_leaves",
        title: t("settings_hub.card_leave_types_title"),
        description: t("settings_hub.card_leave_types_desc"),
        to: "/policies/leave-types",
        icon: CalendarDays,
        tone: "green",
      },
      {
        sectionKey: "policies_leaves",
        title: t("settings_hub.card_leave_chains_title"),
        description: t("settings_hub.card_leave_chains_desc"),
        to: "/policies/approval-chains/leave_request",
        icon: GitBranch,
        tone: "green",
      },
      {
        sectionKey: "policies_leaves",
        title: t("settings_hub.card_leave_statutory_title"),
        description: t("settings_hub.card_leave_statutory_desc"),
        to: "/policies/leave-types",
        icon: ShieldAlert,
        tone: "amber",
        comingSoon: true,
      },
      {
        sectionKey: "policies_leaves",
        title: t("settings_hub.card_pay_advance_title"),
        description: t("settings_hub.card_pay_advance_desc"),
        to: "/policies/leave-types",
        icon: Banknote,
        tone: "teal",
        comingSoon: true,
      },
      {
        sectionKey: "policies_leaves",
        title: t("settings_hub.card_custody_title"),
        description: t("settings_hub.card_custody_desc"),
        to: "/policies/leave-types",
        icon: ClipboardCheck,
        tone: "neutral",
        comingSoon: true,
      },

      // --- HR Policies — EOS (6) ---
      {
        sectionKey: "policies_eos",
        title: t("settings_hub.card_eos_matrix_title"),
        description: t("settings_hub.card_eos_matrix_desc"),
        to: "/policies/eos-rate-matrix",
        icon: Gauge,
        tone: "purple",
      },
      {
        sectionKey: "policies_eos",
        title: t("settings_hub.card_eos_special_title"),
        description: t("settings_hub.card_eos_special_desc"),
        to: "/policies/eos-rate-matrix",
        icon: Banknote,
        tone: "purple",
        comingSoon: true,
      },
      {
        sectionKey: "policies_eos",
        title: t("settings_hub.card_notice_period_title"),
        description: t("settings_hub.card_notice_period_desc"),
        to: "/policies/eos-rate-matrix",
        icon: Clock,
        tone: "amber",
        comingSoon: true,
      },
      {
        sectionKey: "policies_eos",
        title: t("settings_hub.card_article_84_title"),
        description: t("settings_hub.card_article_84_desc"),
        to: "/policies/eos-rate-matrix",
        icon: ShieldAlert,
        tone: "rose",
        comingSoon: true,
      },
      {
        sectionKey: "policies_eos",
        title: t("settings_hub.card_article_80_title"),
        description: t("settings_hub.card_article_80_desc"),
        to: "/policies/eos-rate-matrix",
        icon: ShieldAlert,
        tone: "rose",
        comingSoon: true,
      },
      {
        sectionKey: "policies_eos",
        title: t("settings_hub.card_working_days_title"),
        description: t("settings_hub.card_working_days_desc"),
        to: "/policies/holiday-calendar",
        icon: CalendarDays,
        tone: "teal",
        comingSoon: true,
      },

      // --- HR Policies — Attendance (7) ---
      {
        sectionKey: "policies_attendance",
        title: t("settings_hub.card_shifts_title"),
        description: t("settings_hub.card_shifts_desc"),
        to: "/policies/shifts",
        icon: Clock,
        tone: "teal",
      },
      {
        sectionKey: "policies_attendance",
        title: t("settings_hub.card_rotations_title"),
        description: t("settings_hub.card_rotations_desc"),
        to: "/policies/shifts/rotations",
        icon: Network,
        tone: "teal",
      },
      {
        sectionKey: "policies_attendance",
        title: t("settings_hub.card_excuse_types_title"),
        description: t("settings_hub.card_excuse_types_desc"),
        to: "/policies/excuse-types",
        icon: ClipboardList,
        tone: "amber",
      },
      {
        sectionKey: "policies_attendance",
        title: t("settings_hub.card_ot_catalog_title"),
        description: t("settings_hub.card_ot_catalog_desc"),
        to: "/policies/shifts",
        icon: TrendingUp,
        tone: "indigo",
        comingSoon: true,
      },
      {
        sectionKey: "policies_attendance",
        title: t("settings_hub.card_wfh_title"),
        description: t("settings_hub.card_wfh_desc"),
        to: "/policies/shifts",
        icon: Network,
        tone: "neutral",
        comingSoon: true,
      },
      {
        sectionKey: "policies_attendance",
        title: t("settings_hub.card_holiday_title"),
        description: t("settings_hub.card_holiday_desc"),
        to: "/policies/holiday-calendar",
        icon: CalendarDays,
        tone: "green",
      },
      {
        sectionKey: "policies_attendance",
        title: t("settings_hub.card_attendance_statutory_title"),
        description: t("settings_hub.card_attendance_statutory_desc"),
        to: "/policies/shifts",
        icon: ShieldAlert,
        tone: "amber",
        comingSoon: true,
      },

      // --- HR Policies — Payroll (7) ---
      {
        sectionKey: "policies_payroll",
        title: t("settings_hub.card_coa_title"),
        description: t("settings_hub.card_coa_desc"),
        to: "/settings/integrations",
        icon: Database,
        tone: "indigo",
        comingSoon: true,
      },
      {
        sectionKey: "policies_payroll",
        title: t("settings_hub.card_fin_dimensions_title"),
        description: t("settings_hub.card_fin_dimensions_desc"),
        to: "/settings/integrations",
        icon: Layers,
        tone: "indigo",
        comingSoon: true,
      },
      {
        sectionKey: "policies_payroll",
        title: t("settings_hub.card_cost_centers_title"),
        description: t("settings_hub.card_cost_centers_desc"),
        to: "/settings/integrations",
        icon: Network,
        tone: "teal",
        comingSoon: true,
      },
      {
        sectionKey: "policies_payroll",
        title: t("settings_hub.card_gl_adapters_title"),
        description: t("settings_hub.card_gl_adapters_desc"),
        to: "/settings/integrations",
        icon: Plug,
        tone: "purple",
        comingSoon: true,
      },
      {
        sectionKey: "policies_payroll",
        title: t("settings_hub.card_period_config_title"),
        description: t("settings_hub.card_period_config_desc"),
        to: "/settings/integrations",
        icon: CalendarDays,
        tone: "indigo",
        comingSoon: true,
      },
      {
        sectionKey: "policies_payroll",
        title: t("settings_hub.card_post_workflow_title"),
        description: t("settings_hub.card_post_workflow_desc"),
        to: "/settings/integrations",
        icon: ClipboardCheck,
        tone: "amber",
        comingSoon: true,
      },
      {
        sectionKey: "policies_payroll",
        title: t("settings_hub.card_year_end_title"),
        description: t("settings_hub.card_year_end_desc"),
        to: "/settings/integrations",
        icon: Package,
        tone: "teal",
        comingSoon: true,
      },

      // --- Government Integrations (7) ---
      {
        sectionKey: "government",
        title: t("settings_hub.card_gosi_title"),
        description: t("settings_hub.card_gosi_desc"),
        to: "/settings/integrations",
        icon: Plug,
        tone: "indigo",
      },
      {
        sectionKey: "government",
        title: t("settings_hub.card_mudad_title"),
        description: t("settings_hub.card_mudad_desc"),
        to: "/settings/integrations",
        icon: Plug,
        tone: "indigo",
      },
      {
        sectionKey: "government",
        title: t("settings_hub.card_qiwa_title"),
        description: t("settings_hub.card_qiwa_desc"),
        to: "/settings/integrations",
        icon: Plug,
        tone: "indigo",
        comingSoon: true,
      },
      {
        sectionKey: "government",
        title: t("settings_hub.card_muqeem_title"),
        description: t("settings_hub.card_muqeem_desc"),
        to: "/settings/integrations",
        icon: Plug,
        tone: "indigo",
        comingSoon: true,
      },
      {
        sectionKey: "government",
        title: t("settings_hub.card_zatca_title"),
        description: t("settings_hub.card_zatca_desc"),
        to: "/settings/integrations",
        icon: Plug,
        tone: "indigo",
      },
      {
        sectionKey: "government",
        title: t("settings_hub.card_najiz_title"),
        description: t("settings_hub.card_najiz_desc"),
        to: "/settings/integrations",
        icon: ShieldAlert,
        tone: "rose",
      },
      {
        sectionKey: "government",
        title: t("settings_hub.card_bank_files_title"),
        description: t("settings_hub.card_bank_files_desc"),
        to: "/settings/integrations",
        icon: Banknote,
        tone: "teal",
      },

      // --- AI Configuration (5) ---
      {
        sectionKey: "ai_config",
        title: t("settings_hub.card_ai_behavior_title"),
        description: t("settings_hub.card_ai_behavior_desc"),
        to: "/ai/behavior",
        icon: Sparkles,
        tone: "indigo",
      },
      {
        sectionKey: "ai_config",
        title: t("settings_hub.card_ai_boundaries_title"),
        description: t("settings_hub.card_ai_boundaries_desc"),
        to: "/ai/boundaries",
        icon: ShieldAlert,
        tone: "amber",
      },
      {
        sectionKey: "ai_config",
        title: t("settings_hub.card_ai_persona_title"),
        description: t("settings_hub.card_ai_persona_desc"),
        to: "/ai/persona",
        icon: Sparkles,
        tone: "purple",
      },
      {
        sectionKey: "ai_config",
        title: t("settings_hub.card_ai_knowledge_title"),
        description: t("settings_hub.card_ai_knowledge_desc"),
        to: "/ai/knowledge",
        icon: Database,
        tone: "teal",
      },
      {
        sectionKey: "ai_config",
        title: t("settings_hub.card_ai_sandbox_title"),
        description: t("settings_hub.card_ai_sandbox_desc"),
        to: "/ai/sandbox",
        icon: Play,
        tone: "neutral",
      },

      // --- Hardware Integrations (1) ---
      {
        sectionKey: "hardware",
        title: t("settings_hub.card_biometric_title"),
        description: t("settings_hub.card_biometric_desc"),
        to: "/integrations/biometric",
        icon: Eye,
        tone: "teal",
      },

      // --- Imports + Bulk Operations (4) ---
      {
        sectionKey: "imports",
        title: t("settings_hub.card_import_generic_title"),
        description: t("settings_hub.card_import_generic_desc"),
        to: "/operations/import",
        icon: UploadCloud,
        tone: "indigo",
      },
      {
        sectionKey: "imports",
        title: t("settings_hub.card_import_history_title"),
        description: t("settings_hub.card_import_history_desc"),
        to: "/settings/imports",
        icon: ClipboardList,
        tone: "neutral",
      },
      {
        sectionKey: "imports",
        title: t("settings_hub.card_import_pending_title"),
        description: t("settings_hub.card_import_pending_desc"),
        to: "/settings/imports",
        icon: AlertCircle,
        tone: "amber",
      },
      {
        sectionKey: "imports",
        title: t("settings_hub.card_import_templates_title"),
        description: t("settings_hub.card_import_templates_desc"),
        to: "/settings/imports",
        icon: ClipboardCheck,
        tone: "neutral",
        comingSoon: true,
      },

      // --- System (2) ---
      {
        sectionKey: "system",
        title: t("settings_hub.card_audit_log_title"),
        description: t("settings_hub.card_audit_log_desc"),
        to: "/settings/audit-log",
        icon: Database,
        tone: "neutral",
      },
      {
        sectionKey: "system",
        title: t("settings_hub.card_billing_title"),
        description: t("settings_hub.card_billing_desc"),
        to: "/settings/billing",
        icon: BadgeDollarSign,
        tone: "indigo",
      },
    ];
    return cards;
  }, [t, entitiesCount]);

  const filteredCards = useMemo(() => {
    if (!searchTerm.trim()) return allCards;
    const needle = searchTerm.toLowerCase();
    return allCards.filter(
      (c) =>
        c.title.toLowerCase().includes(needle) ||
        c.description.toLowerCase().includes(needle) ||
        c.sectionKey.toLowerCase().includes(needle),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const sectionsToRender = SETTINGS_SECTIONS;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("settings_hub.title")}
        subtitle={t("settings_hub.subtitle")}
      />

      <div className="relative max-w-md">
        <input
          type="search"
          placeholder={t("settings_hub.search_placeholder")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full rounded-lg border border-border-subtle bg-surface-default px-3 py-2 text-sm placeholder:text-text-tertiary focus:border-ai focus:outline-none focus:ring-2 focus:ring-ai/20"
          aria-label={t("settings_hub.search_placeholder")}
        />
      </div>

      {searchTerm.trim() && filteredCards.length === 0 && (
        <EmptyState headline={t("settings_hub.empty_no_match")} />
      )}

      {sectionsToRender.map((section) => {
        const cards = filteredCards.filter(
          (c) => c.sectionKey === section.key,
        );
        // Hide empty sections during search to keep results focused.
        if (searchTerm.trim() && cards.length === 0) return null;
        return (
          <SettingsSection key={section.key} title={t(section.titleKey)}>
            {cards.length === 0 && !searchTerm.trim() ? (
              <p className="col-span-full text-xs text-text-tertiary italic">
                {t("settings_hub.coming_soon")}
              </p>
            ) : (
              cards.map((card) => (
                <SettingsEntryCard
                  key={card.title}
                  icon={card.icon}
                  title={card.title}
                  description={card.description}
                  to={card.to}
                  meta={card.meta}
                  tone={card.tone}
                  comingSoon={card.comingSoon}
                />
              ))
            )}
          </SettingsSection>
        );
      })}
    </div>
  );
}

// Light defensive use of imports so unused-import lint passes when a
// landing temporarily drops an icon during scope changes.
void Bell;
void Package;
void PageHeader;
